import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Heart, CheckCircle, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RsvpSection = () => {
  const { t, i18n } = useTranslation();
  const isHe = i18n.language === "he";

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone_code: "+972",
    phone: "",
    attending: true,
    attending_shabbat: true,
    guest_count: 1,
    guests: [],
    message: ""
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t("required");
    if (!form.last_name.trim()) e.last_name = t("required");
    if (!form.phone.trim()) e.phone = t("required");
    else if (!/^[0-9]{7,15}$/.test(form.phone.replace(/[\s-]/g, ''))) e.phone = t("invalid_email");
    if ((form.attending || form.attending_shabbat) && form.guest_count > 1) {
      form.guests.forEach((g, i) => {
        if (!g.first_name.trim()) e[`guest_${i}_first`] = t("required");
        if (!g.last_name.trim()) e[`guest_${i}_last`] = t("required");
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus("submitting");
    try {
      await axios.post(`${API}/rsvp`, {
        ...form,
        email: `${form.phone_code}${form.phone.replace(/[\s-]/g, '')}`,
        guest_count: (form.attending || form.attending_shabbat) ? form.guest_count : 0,
        guests: (form.attending || form.attending_shabbat) && form.guest_count > 1 ? form.guests : [],
        lang: i18n.language
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      if (err.response?.status === 409) {
        setErrorMsg(t("error_already"));
      } else {
        setErrorMsg(t("error_generic"));
      }
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const updateGuestCount = (newCount) => {
    const extraGuests = newCount - 1;
    setForm(prev => {
      const currentGuests = [...prev.guests];
      while (currentGuests.length < extraGuests) {
        currentGuests.push({ first_name: "", last_name: "" });
      }
      return {
        ...prev,
        guest_count: newCount,
        guests: currentGuests.slice(0, extraGuests)
      };
    });
  };

  const updateGuest = (index, field, value) => {
    setForm(prev => {
      const updated = [...prev.guests];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, guests: updated };
    });
    const errKey = `guest_${index}_${field === "first_name" ? "first" : "last"}`;
    if (errors[errKey]) setErrors(prev => ({ ...prev, [errKey]: undefined }));
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
  };

  if (status === "success") {
    return (
      <section data-testid="rsvp-section" className="bg-cream py-24 md:py-32 px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center"
        >
          <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-gold" />
          </div>
          <h3 data-testid="rsvp-success" className="font-serif text-charcoal text-3xl md:text-4xl mb-4">
            {t("success_title")}
          </h3>
          <p className="font-sans text-secondary text-base leading-relaxed">
            {form.attending ? t("success_attending") : t("success_not_attending")}
          </p>
          <div className="w-16 h-px bg-gold mx-auto mt-8" />
        </motion.div>
      </section>
    );
  }

  return (
    <section data-testid="rsvp-section" className="bg-cream pt-24 pb-8 md:pt-32 md:pb-12 px-6" id="rsvp">
      <motion.div
        className="max-w-xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {/* Header */}
        <motion.div variants={fadeIn} className="text-center mb-16">
          <Heart className="w-6 h-6 text-gold mx-auto mb-6" strokeWidth={1} />
          <h2 className="font-serif text-charcoal text-3xl md:text-5xl tracking-tight mb-4">
            {t("rsvp_title")}
          </h2>
          <p className="font-sans text-secondary text-sm tracking-[0.2em] uppercase">
            {t("rsvp_subtitle")}
          </p>
          <div className="w-16 h-px bg-gold mx-auto mt-6 mb-6" />
          <p className="font-serif text-secondary/60 text-sm md:text-base italic">
            {t("rsvp_deadline")}
          </p>
        </motion.div>

        {/* Error banner */}
        <AnimatePresence>
          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-sm p-4 mb-8"
              data-testid="rsvp-error"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="font-sans text-red-700 text-sm">{errorMsg}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form */}
        <motion.form
          variants={fadeIn}
          onSubmit={handleSubmit}
          className="space-y-6"
          data-testid="rsvp-form"
        >
          {/* Name row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-sans text-charcoal text-sm tracking-wider mb-2">
                {t("last_name")} *
              </label>
              <input
                data-testid="rsvp-last-name"
                type="text"
                value={form.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                className={`w-full bg-white border ${errors.last_name ? 'border-red-400' : 'border-sand'} rounded-sm px-4 py-3 font-sans text-charcoal text-base focus:outline-none focus:border-gold transition-colors`}
                dir={isHe ? "rtl" : "ltr"}
              />
              {errors.last_name && <p className="text-red-500 text-xs mt-1 font-sans">{errors.last_name}</p>}
            </div>
            <div>
              <label className="block font-sans text-charcoal text-sm tracking-wider mb-2">
                {t("first_name")} *
              </label>
              <input
                data-testid="rsvp-first-name"
                type="text"
                value={form.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                className={`w-full bg-white border ${errors.first_name ? 'border-red-400' : 'border-sand'} rounded-sm px-4 py-3 font-sans text-charcoal text-base focus:outline-none focus:border-gold transition-colors`}
                dir={isHe ? "rtl" : "ltr"}
              />
              {errors.first_name && <p className="text-red-500 text-xs mt-1 font-sans">{errors.first_name}</p>}
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block font-sans text-charcoal text-sm tracking-wider mb-2">
              {t("email")} *
            </label>
            <div className="flex gap-2" dir="ltr">
              <select
                data-testid="rsvp-phone-code"
                value={form.phone_code}
                onChange={(e) => handleChange("phone_code", e.target.value)}
                className="bg-white border border-sand rounded-sm px-2 py-3 font-sans text-charcoal text-sm focus:outline-none focus:border-gold transition-colors w-[100px] flex-shrink-0"
              >
                <option value="+972">+972 IL</option>
                <option value="+33">+33 FR</option>
              </select>
              <input
                data-testid="rsvp-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className={`flex-1 bg-white border ${errors.phone ? 'border-red-400' : 'border-sand'} rounded-sm px-4 py-3 font-sans text-charcoal text-base focus:outline-none focus:border-gold transition-colors`}
                placeholder=""
              />
            </div>
            {errors.phone && <p className="text-red-500 text-xs mt-1 font-sans">{errors.phone}</p>}
          </div>

          {/* Attendance - Wedding */}
          <div>
            <label className="block font-sans text-charcoal text-sm tracking-wider mb-3">
              {t("attending")} *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="rsvp-attending-yes"
                onClick={() => handleChange("attending", true)}
                className={`py-3 px-4 rounded-sm border text-sm font-sans tracking-wider transition-all duration-300 ${
                  form.attending
                    ? 'bg-gold text-white border-gold'
                    : 'bg-white text-charcoal border-sand hover:border-gold'
                }`}
              >
                {t("will_attend")}
              </button>
              <button
                type="button"
                data-testid="rsvp-attending-no"
                onClick={() => handleChange("attending", false)}
                className={`py-3 px-4 rounded-sm border text-sm font-sans tracking-wider transition-all duration-300 ${
                  !form.attending
                    ? 'bg-charcoal text-white border-charcoal'
                    : 'bg-white text-charcoal border-sand hover:border-charcoal'
                }`}
              >
                {t("will_not_attend")}
              </button>
            </div>
          </div>

          {/* Attendance - Shabbat Hatane */}
          <div>
            <label className="block font-sans text-charcoal text-sm tracking-wider mb-3">
              {t("attending_shabbat")} *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="rsvp-attending-shabbat-yes"
                onClick={() => handleChange("attending_shabbat", true)}
                className={`py-3 px-4 rounded-sm border text-sm font-sans tracking-wider transition-all duration-300 ${
                  form.attending_shabbat
                    ? 'bg-gold text-white border-gold'
                    : 'bg-white text-charcoal border-sand hover:border-gold'
                }`}
              >
                {t("will_attend")}
              </button>
              <button
                type="button"
                data-testid="rsvp-attending-shabbat-no"
                onClick={() => handleChange("attending_shabbat", false)}
                className={`py-3 px-4 rounded-sm border text-sm font-sans tracking-wider transition-all duration-300 ${
                  !form.attending_shabbat
                    ? 'bg-charcoal text-white border-charcoal'
                    : 'bg-white text-charcoal border-sand hover:border-charcoal'
                }`}
              >
                {t("will_not_attend")}
              </button>
            </div>
          </div>

          {/* Guest count */}
          <AnimatePresence>
            {(form.attending || form.attending_shabbat) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <label className="block font-sans text-charcoal text-sm tracking-wider mb-2">
                  {t("guest_count")}
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    data-testid="rsvp-guest-minus"
                    onClick={() => updateGuestCount(Math.max(1, form.guest_count - 1))}
                    className="w-10 h-10 rounded-sm border border-sand bg-white text-charcoal flex items-center justify-center hover:border-gold transition-colors text-lg"
                  >
                    -
                  </button>
                  <span
                    data-testid="rsvp-guest-count"
                    className="font-serif text-charcoal text-2xl w-12 text-center tabular-nums"
                  >
                    {form.guest_count}
                  </span>
                  <button
                    type="button"
                    data-testid="rsvp-guest-plus"
                    onClick={() => updateGuestCount(Math.min(20, form.guest_count + 1))}
                    className="w-10 h-10 rounded-sm border border-sand bg-white text-charcoal flex items-center justify-center hover:border-gold transition-colors text-lg"
                  >
                    +
                  </button>
                </div>

                {/* Guest name fields */}
                {form.guest_count > 1 && (
                  <div className="mt-6 space-y-4">
                    {form.guests.map((guest, i) => (
                      <div key={i} className="border border-sand/60 rounded-sm p-4 bg-white/50">
                        <p className="font-sans text-secondary text-xs tracking-wider mb-3">
                          {t("guest_label")} {i + 1}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <input
                              data-testid={`guest-${i}-last-name`}
                              type="text"
                              placeholder={t("guest_last_name")}
                              value={guest.last_name}
                              onChange={(e) => updateGuest(i, "last_name", e.target.value)}
                              className={`w-full bg-white border ${errors[`guest_${i}_last`] ? 'border-red-400' : 'border-sand'} rounded-sm px-3 py-2.5 font-sans text-charcoal text-sm focus:outline-none focus:border-gold transition-colors placeholder:text-secondary/40`}
                              dir={isHe ? "rtl" : "ltr"}
                            />
                            {errors[`guest_${i}_last`] && <p className="text-red-500 text-xs mt-1 font-sans">{errors[`guest_${i}_last`]}</p>}
                          </div>
                          <div>
                            <input
                              data-testid={`guest-${i}-first-name`}
                              type="text"
                              placeholder={t("guest_first_name")}
                              value={guest.first_name}
                              onChange={(e) => updateGuest(i, "first_name", e.target.value)}
                              className={`w-full bg-white border ${errors[`guest_${i}_first`] ? 'border-red-400' : 'border-sand'} rounded-sm px-3 py-2.5 font-sans text-charcoal text-sm focus:outline-none focus:border-gold transition-colors placeholder:text-secondary/40`}
                              dir={isHe ? "rtl" : "ltr"}
                            />
                            {errors[`guest_${i}_first`] && <p className="text-red-500 text-xs mt-1 font-sans">{errors[`guest_${i}_first`]}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Message */}
          <div>
            <label className="block font-sans text-charcoal text-sm tracking-wider mb-2">
              {t("message")}
            </label>
            <textarea
              data-testid="rsvp-message"
              value={form.message}
              onChange={(e) => handleChange("message", e.target.value)}
              rows={3}
              placeholder={t("message_placeholder")}
              className="w-full bg-white border border-sand rounded-sm px-4 py-3 font-sans text-charcoal text-base focus:outline-none focus:border-gold transition-colors resize-none placeholder:text-secondary/40"
              dir={isHe ? "rtl" : "ltr"}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            data-testid="rsvp-submit"
            disabled={status === "submitting"}
            className="w-full py-4 bg-gold text-white font-sans text-sm tracking-[0.2em] uppercase rounded-sm hover:bg-[#b8933f] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === "submitting" ? t("submitting") : t("submit")}
          </button>
        </motion.form>
      </motion.div>
    </section>
  );
};

export default RsvpSection;
